import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ApiError } from '../api/client';
import { listDashboards, type DashboardSummary } from '../api/dashboards';
import { AppShell } from '../components/layout/AppShell';
import { Card, EmptyState, ErrorState, LoadingState } from '../components/ui';
import './DashboardsPage.css';

type State = 'loading' | 'ready' | 'error';

/** `/dashboards` — every dashboard the user has brewed. */
export function DashboardsPage() {
  const navigate = useNavigate();
  const [state, setState] = useState<State>('loading');
  const [dashboards, setDashboards] = useState<DashboardSummary[]>([]);
  const [error, setError] = useState<ApiError | null>(null);

  const load = useCallback(async () => {
    try {
      setDashboards(await listDashboards());
      setState('ready');
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : null);
      setState('error');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const header = (
    <div className="sc-page-header">
      <div>
        <h1>Dashboards</h1>
        <p>A Quick Brew turns a dataset into a dashboard in one step.</p>
      </div>
    </div>
  );

  if (state === 'loading') {
    return (
      <AppShell header={header}>
        <LoadingState label="Loading your dashboards…" />
      </AppShell>
    );
  }

  if (state === 'error') {
    return (
      <AppShell header={header}>
        <ErrorState
          title="We could not load your dashboards"
          description={error?.message}
          requestId={error?.requestId}
          onRetry={() => void load()}
        />
      </AppShell>
    );
  }

  return (
    <AppShell header={header}>
      {dashboards.length === 0 ? (
        <EmptyState
          title="No dashboards yet"
          description="Open one of your datasets and brew one — Simple Coffee picks the charts."
          actionLabel="Go to Beans"
          onAction={() => navigate('/datasets')}
        />
      ) : (
        <div className="sc-dashboards">
          {dashboards.map((dashboard) => (
            <Card key={dashboard.id} title={dashboard.title} description={dashboard.description}>
              <p className="sc-dashboards__meta">
                {dashboard.block_count} block{dashboard.block_count === 1 ? '' : 's'}
              </p>
              <Link to={`/dashboards/${dashboard.id}`}>Open dashboard</Link>
            </Card>
          ))}
        </div>
      )}
    </AppShell>
  );
}
